import { isPreviewMode } from "@/lib/previewContext";

/**
 * Folders that should be excluded from:
 * - Sitemap generation
 * - Static page generation
 * - Public-facing routes (redirected to "/" in production)
 *
 * These folders are only visible in preview/development environments but are
 * still accessible for data fetching (e.g., global-components for header/footer).
 */
export const SITEMAP_EXCLUDED_FOLDERS = [
  "global-components", // Global UI components and data (accessible for data fetching)
  "configuration", // Configuration data (accessible for data fetching)
  "demo", // Demo pages
  "temp", // Temporary pages
] as const;

/**
 * Folders that should be excluded from data fetching operations like
 * fetchStories, fetchArticles, etc.
 *
 * Note: "global-components" and "configuration" are NOT in this list because
 * they need to be accessible for fetching global data like header/footer.
 */
export const DATA_EXCLUDED_FOLDERS = [
  "demo", // Demo pages
  "temp", // Temporary pages
] as const;

/**
 * Legacy constant for backward compatibility.
 * @deprecated Use SITEMAP_EXCLUDED_FOLDERS or DATA_EXCLUDED_FOLDERS instead
 */
export const EXCLUDED_FOLDERS = SITEMAP_EXCLUDED_FOLDERS;

/**
 * Checks if a given slug/path should be excluded from sitemap/routes.
 * Handles various path formats:
 * - "/global-components/something"
 * - "parkmobile/global-components"
 * - "global-components"
 *
 * @param slug - The full slug or path to check
 * @returns true if the slug should be excluded from sitemap/routes, false otherwise
 */
export function isExcludedFolder(slug: string): boolean {
  if (!slug) return false;

  // Normalize the slug by removing leading/trailing slashes and splitting by /
  const normalizedSlug = slug.replace(/^\/+|\/+$/g, "");
  const parts = normalizedSlug.split("/");

  // Check if any part of the path matches an excluded folder
  return SITEMAP_EXCLUDED_FOLDERS.some((folder) => parts.includes(folder));
}

/**
 * Checks if a given slug/path should be excluded from data fetching operations.
 *
 * @param slug - The full slug or path to check
 * @returns true if the slug should be excluded from data fetching, false otherwise
 */
export function isDataExcludedFolder(slug: string): boolean {
  if (!slug) return false;

  // Normalize the slug by removing leading/trailing slashes and splitting by /
  const normalizedSlug = slug.replace(/^\/+|\/+$/g, "");
  const parts = normalizedSlug.split("/");

  // Check if any part of the path matches a data-excluded folder
  return DATA_EXCLUDED_FOLDERS.some((folder) => parts.includes(folder));
}

/**
 * Determines if excluded folders should be visible based on environment.
 * Excluded folders are visible in preview/dev mode, hidden in production.
 * @returns true if excluded folders should be shown
 */
export function shouldShowExcludedFolders(): boolean {
  // Show excluded folders in preview mode or development
  return isPreviewMode() || process.env.NODE_ENV === "development";
}
